import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLocalization, type SupportedLocale, type TranslationKey } from "../../lib/i18n";
import { formatPublicSportsDate, getPublicSportsCalendar, type PublicSportsCalendarRow } from "../../lib/publicSportsCalendar";

type CalendarSelectionProps = {
  selectedSportKey?: TranslationKey;
  onSelectSport?: (sportKey: TranslationKey) => void;
  availableSportKeys?: readonly TranslationKey[];
};

type CalendarPanelProps = CalendarSelectionProps & {
  rows: PublicSportsCalendarRow[];
  locale: SupportedLocale;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
};

export function PublicSportsCalendarPanel({ rows, locale, t, selectedSportKey, onSelectSport, availableSportKeys }: CalendarPanelProps) {
  return (
    <section aria-labelledby="landing-sports-calendar-title" className="mt-7 max-w-[520px] border-y border-white/15 bg-gradient-to-r from-black/60 to-transparent py-5">
      <h2 id="landing-sports-calendar-title" className="font-display text-[13px] font-bold uppercase tracking-[0.18em] text-white/65">
        {t("guest.landing.sports.title")}
      </h2>
      <ul className="mt-5">
        {rows.map((row) => (
          <li key={row.league} className="relative pb-5 pl-5 last:pb-0 before:absolute before:bottom-0 before:left-[3px] before:top-3 before:w-px before:bg-white/15 last:before:hidden">
            <span aria-hidden="true" className={`absolute left-0 top-3 h-[7px] w-[7px] rounded-full ${(onSelectSport ? selectedSportKey === row.sportKey : row.currentPhaseKey && !row.upcoming) ? "bg-[#F5A623] shadow-[0_0_10px_rgba(245,166,35,0.35)]" : "border border-white/50 bg-black"}`} />
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3">
                {onSelectSport ? (
                  <button
                    type="button"
                    aria-pressed={selectedSportKey === row.sportKey}
                    disabled={availableSportKeys !== undefined && !availableSportKeys.includes(row.sportKey)}
                    onClick={() => onSelectSport(row.sportKey)}
                    className={`min-h-11 rounded text-left font-display text-xl font-bold uppercase tracking-[0.02em] transition-colors hover:text-[#F5A623] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:opacity-40 ${selectedSportKey === row.sportKey ? "text-[#F5A623]" : "text-white"}`}
                  >{t(row.sportKey)}</button>
                ) : <span className="font-display text-xl font-bold uppercase tracking-[0.02em] text-white">{t(row.sportKey)}</span>}
                <a
                  href={row.leagueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("guest.landing.sports.leagueLink", { league: row.league })}
                  className="inline-flex min-h-8 items-center gap-0.5 rounded font-mono text-[11px] font-bold text-white/55 transition-colors hover:text-[#F5A623] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                >
                  {row.league}
                  <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                </a>
              </div>
            </div>
            {row.currentPhaseKey && (
              <p className="flex flex-wrap items-center gap-x-2 text-[13px] leading-6 text-white/65">
                <span>{t(row.currentPhaseKey)}</span>
                <span aria-hidden="true" className="text-white/30">/</span>
                <span className="font-display text-[11px] font-bold uppercase tracking-[0.1em] text-[#F5A623]">
                  {t("guest.landing.sports.now")}
                </span>
              </p>
            )}
            {row.upcoming ? (
              <p className="mt-0.5 text-[13px] font-semibold leading-6 text-white/90">
                {t(row.upcoming.labelKey, { date: formatPublicSportsDate(row.upcoming.date, locale) })}
                {row.upcoming.planned && <span className="font-normal text-white/55">{` · ${t("guest.landing.sports.planned")}`}</span>}
              </p>
            ) : !row.currentPhaseKey && (
              <p className="mt-1 text-[13px] leading-6 text-white/65">{t("guest.landing.sports.schedule")}</p>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-5 text-xs leading-5 text-white/55">{t("guest.landing.sports.helper")}</p>
    </section>
  );
}

export function PublicSportsCalendar(selection: CalendarSelectionProps) {
  const { locale, t } = useLocalization();
  const [rows, setRows] = useState(() => getPublicSportsCalendar());
  useEffect(() => {
    const interval = window.setInterval(() => setRows(getPublicSportsCalendar()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  return <PublicSportsCalendarPanel rows={rows} locale={locale} t={t} {...selection} />;
}
